import React, { useState, useMemo } from "react";
import { Box, Typography, IconButton, Stack } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { CardDesign } from "./UserViewCardDesign";
import {
  useAddToDeckCard,
  useBookmarkCard,
} from "../../../../../services/api/card.api";
import { enqueueSnackbar } from "notistack";
import { useQueryClient } from "@tanstack/react-query"; // ✅ Import this at the top
import { useCardData } from "../../../../../services/store/useCardData";
import { useCardStore } from "../../../../../services/store/UseCardStore";
import GuestSignInModal from "../../../../../pages/Auth/GuestSignInModal";
import { useAuth } from "../../../../../hooks";

interface RenderUserDeckProps {
  suit: string;
  title?: string;
  fromAddToDeck?: boolean;
}

const RenderUserDeck = ({
  suit,
  title = "My Decks",
  fromAddToDeck = false,
}: RenderUserDeckProps) => {
  const navigate = useNavigate();
  const scrollRef = React.useRef<HTMLDivElement>(null);

  const [hoveredCardId, setHoveredCardId] = useState<string | null>(null);

  const { allCards, deckIds, bookmarkedIds, isLoading } = useCardData();
  const { update: bookmarkCard } = useBookmarkCard({ key: ["bookmark-card"] });
  const { update: addedToDeckCard } = useAddToDeckCard({
    key: ["add-to-deck"],
  });

  const { userInfo } = useAuth();
  const isGuest = userInfo ? false : true;
  // const isGuest = localStorage.getItem("isGuest") === "true" ? true : false;
  const [isGuestModalOpen, setIsGuestModalOpen] = useState(false);

  const queryClient = useQueryClient(); // ✅ Initialize it

  const { setMyDeck, setBookmarked } = useCardStore();

  const handleAddToDeck = (id?: string) => {
    if (!id) return;

    const isAddedtoDeck = deckIds.includes(id);
    const optimisticUpdatedIds = isAddedtoDeck
      ? deckIds.filter((bid) => bid !== id)
      : [...deckIds, id];

    // Optimistically update UI
    setMyDeck(optimisticUpdatedIds);

    addedToDeckCard(
      { card_id: parseInt(id) },
      {
        onSuccess: (response: any) => {
          const message =
            response.message || "Card added to your deck successfully!";
          const removedFromDeck = message.toLowerCase().includes("removed");

          enqueueSnackbar(message, {
            variant: removedFromDeck ? "warning" : "success",
          });

          queryClient.invalidateQueries({ queryKey: ["add-to-deck"] });
        },
        onError: (error: any) => {
          const backendMessage =
            error?.response?.data?.message ||
            error?.message ||
            "Failed to add the card to deck.";

          enqueueSnackbar(backendMessage, { variant: "error" });

          // Rollback optimistic update on failure
          setMyDeck(deckIds);
        },
      }
    );
  };

  const handleBookmark = (id?: string) => {
    if (!id) return;

    const isBookmarked = bookmarkedIds.includes(id);

    const optimisticUpdatedIds = isBookmarked
      ? bookmarkedIds.filter((bid) => bid !== id)
      : [...bookmarkedIds, id];

    // Optimistically update UI
    setBookmarked(optimisticUpdatedIds);
    bookmarkCard(
      { card_id: parseInt(id) },
      {
        onSuccess: (response) => {
          const message = response.message || "Card bookmarked successfully!";
          const isUnbookmarked =
            message.toLowerCase().includes("removed") ||
            message.toLowerCase().includes("unbookmarked");
          enqueueSnackbar(message, {
            variant: isUnbookmarked ? "warning" : "success", // 🟡 warning = unbookmarked, 🟢 success = bookmarked
          });
          // ✅ Immediately refetch bookmarked cards so UI updates
          queryClient.invalidateQueries({ queryKey: ["bookmark-card"] });
        },
        onError: (error: any) => {
          const backendMessage =
            error?.response?.data?.message ||
            error?.message ||
            "Failed to bookmark the card.";
          enqueueSnackbar(backendMessage, { variant: "error" });

          // Rollback optimistic update on failure
          setBookmarked(bookmarkedIds);
        },
      }
    );
  };

  const filteredCards = useMemo(() => {
    return allCards.filter(
      (card) =>
        deckIds.includes(card.id) &&
        (suit === "recommended" || card.suit === suit)
    );
  }, [allCards, deckIds, suit]);

  const scrollBy = (offset: number) => {
    scrollRef.current?.scrollBy({ left: offset, behavior: "smooth" });
  };

  if (isLoading) {
    return (
      <Box textAlign="center" py={8}>
        <Typography variant="body2">Loading your deck...</Typography>
      </Box>
    );
  }

  if (!filteredCards.length) {
    return (
      <Box textAlign="center" py={8}>
        <Typography variant="body2">No cards found for this deck.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 6 }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography sx={{ font: "var(--h5)", color: "var(--neutral800)" }}>
          {title}
        </Typography>
        <Stack direction="row" spacing={1}>
          <IconButton onClick={() => scrollBy(-400)} sx={navBtnStyle}>
            <ChevronLeft size={18} />
          </IconButton>
          <IconButton onClick={() => scrollBy(400)} sx={navBtnStyle}>
            <ChevronRight size={18} />
          </IconButton>
        </Stack>
      </Stack>

      <Box
        ref={scrollRef}
        sx={{
          display: "flex",
          overflowX: "auto",
          scrollBehavior: "smooth",
          gap: 3,
          pr: 1,
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        {filteredCards.map((card) => (
          <Box
            key={card.id}
            onMouseEnter={() => setHoveredCardId(card.id)}
            onMouseLeave={() => setHoveredCardId(null)}
            sx={{
              minWidth: 300,
              flex: "0 0 auto", // ✅ Keep fixed width, allow horizontal scroll
              display: "flex",
              justifyContent: "center",
            }}
          >
            <CardDesign
              card={{
                id: card.id,
                title: card.title,
                description: card.short_description,
                suit: card.suit,
                categories: card.category,
                price: card.price ?? 0,
                duration: card.duration ?? 30,
                primary_thumbnail: card.primary_thumbnail,
                talent: card.talent,
                startDateTime: card.start_date_time,
                product_type: card.product?.type,
                allow_instance_booking: card.allow_instance_booking,
              }}
              onClickViewDetail={() => navigate(`/cards/${card.id}`)}
              isHovered={hoveredCardId === card.id}
              onClickBooKNow={() => navigate(`/taas-suits/${card.id}`)}
              myDeckCardIds={deckIds}
              onAddtoDeck={() =>
                !isGuest
                  ? handleAddToDeck?.(card.id)
                  : setIsGuestModalOpen(true)
              }
              onBookMark={() =>
                !isGuest ? handleBookmark?.(card.id) : setIsGuestModalOpen(true)
              }
              bookmarkedCardIds={bookmarkedIds}
              fromAddToDeck={fromAddToDeck}
            />
          </Box>
        ))}
      </Box>
      <GuestSignInModal
        open={isGuestModalOpen}
        onClose={() => setIsGuestModalOpen(false)}
      />
    </Box>
  );
};

const navBtnStyle = {
  border: "1px solid #ddd",
  borderRadius: 2,
  backgroundColor: "#fff",
  "&:hover": { backgroundColor: "#f3f4f6" },
};

export default RenderUserDeck;
